import { create } from "zustand";
import { createLogger } from "debug/DebugingLogger";
import { pollingAllowed } from "@/components/hooks/activityGate";
import { fetchModelsInTrash } from "@/components/main/utils/models/ModelServices";
import { fetchTrashedSpaces, type TrashedSpace } from "@/lib/api/organizations";
import { activeOrgForRequests } from "@/components/main/stores/orgContextStore";
import { TrashModel } from "target_wasm";

const log = createLogger("useTrashStore");

interface TrashState {
  trashModels: TrashModel[];
  /// D52: doomed spaces the caller manages — the Trash's group headers.
  trashedSpaces: TrashedSpace[];
  /// The org the lists were fetched for. The Trash is org-scoped (S6c/D41),
  /// so a switch is a new list, not a filter over the one already rendered.
  trashOrgId: string | null;
  isLoadingTrashModels: boolean;
  lastTrashModelsFetch: number | null;
  fetchTrashModels: (force?: boolean) => Promise<void>;
  refreshTrashModels: () => Promise<void>;
  updateTrashModel: (modelId: string, updates: Partial<TrashModel>) => void;
  removeTrashModel: (modelId: string) => void;
  /// Restore space: the headers and every model trashed with them leave together.
  removeTrashedSpaces: (spaceIds: string[]) => void;
  startPolling: () => () => void;
}

export const useTrashStore = create<TrashState>((set, get) => ({
  trashModels: [],
  trashedSpaces: [],
  trashOrgId: null,
  isLoadingTrashModels: false,
  lastTrashModelsFetch: null,

  fetchTrashModels: async (force = false) => {
    const org = activeOrgForRequests();
    const orgId = org.kind === "org" ? org.orgId : null;
    const { lastTrashModelsFetch, isLoadingTrashModels, trashOrgId } = get();
    const now = Date.now();

    // Both doors require a context. There is always one once `init()` has
    // resolved, so the only way here is the boot window — wait for it rather
    // than spend a 400, and leave `lastTrashModelsFetch` null so the page
    // keeps its loader. The page re-runs this when `activeOrgId` lands.
    if (!orgId) return;

    if (orgId !== trashOrgId) {
      // The previous org's rows must not linger through the first fetch, and
      // the loader is right: nothing here belongs to the new context yet.
      set({
        trashModels: [],
        trashedSpaces: [],
        trashOrgId: orgId,
        lastTrashModelsFetch: null,
        isLoadingTrashModels: true,
      });
    } else if (
      isLoadingTrashModels ||
      (!force && lastTrashModelsFetch && now - lastTrashModelsFetch < 1000)
    ) {
      return;
    } else if (lastTrashModelsFetch === null) {
      // Only show loading state on initial load, not background refreshes
      set({ isLoadingTrashModels: true });
    }

    // A slow answer that lost a race with an org switch is not this org's.
    const stillCurrent = () => {
      const now = activeOrgForRequests();
      return (
        now.kind === "org" && now.orgId === orgId && get().trashOrgId === orgId
      );
    };

    try {
      const [models, spaces] = await Promise.all([
        fetchModelsInTrash(orgId),
        fetchTrashedSpaces(orgId).catch((error: unknown) => {
          log.error("Failed to fetch trashed spaces:", error);
          return get().trashedSpaces;
        }),
      ]);
      if (!stillCurrent()) return;
      set({
        trashModels: models,
        trashedSpaces: spaces,
        isLoadingTrashModels: false,
        lastTrashModelsFetch: now,
      });
    } catch (error) {
      log.error("Failed to fetch trash models:", error);
      if (stillCurrent()) set({ isLoadingTrashModels: false });
      throw error;
    }
  },

  refreshTrashModels: async () => {
    await get().fetchTrashModels(true);
  },

  updateTrashModel: (modelId, updates) => {
    set((state) => ({
      trashModels: state.trashModels.map((model) =>
        model.model_id_js === modelId
          ? ({ ...model, ...updates } as TrashModel)
          : model,
      ),
    }));
  },

  removeTrashModel: (modelId) => {
    set((state) => ({
      trashModels: state.trashModels.filter(
        (model) => model.model_id_js !== modelId,
      ),
    }));
  },

  removeTrashedSpaces: (spaceIds) => {
    const gone = new Set(spaceIds);
    set((state) => ({
      trashedSpaces: state.trashedSpaces.filter((s) => !gone.has(s.space_id)),
      trashModels: state.trashModels.filter(
        (model) => !gone.has(model.via_space_id_js ?? ""),
      ),
    }));
  },

  startPolling: () => {
    get().fetchTrashModels(true);
    const interval = setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      get().fetchTrashModels();
    }, 30_000);

    return () => clearInterval(interval);
  },
}));
